/**
 * DashboardHeader — top bar with logo, user menu, notifications, tabs.
 *
 * Extracted from DashboardPage.tsx to reduce the God Component.
 */

import zapLogo from "figma:asset/f3ea20a5ac1d9a14ff41e77e5c2314b781a6a6e5.png";
import {
  Plus,
  Users,
  LayoutTemplate,
  UserCircle,
  LogOut,
  Crown,
  User,
  Moon,
  Sun,
  Archive,
  MessageSquare,
  Settings,
  Image as ImageIcon,
  ClipboardList,
  ClipboardCheck,
  Lock,
  Zap,
  Upload,
} from "lucide-react";
import { NotificationBell } from "../shared/NotificationBell";
import { SearchTriggerButton } from "../shared/CommandPalette";
import { TabButton } from "./DashboardWidgets";
import { ROLE_LABELS, ROLE_BADGE_COLORS } from "./dashboardConstants";
import type { DashboardTab } from "./dashboardConstants";

/* ================================================================
   Props
   ================================================================ */

interface DashboardHeaderProps {
  user: {
    username: string;
    displayName?: string;
    role: string;
  };
  userAvatar?: string;
  isDark: boolean;
  toggleTheme: () => void;
  isAdmin: boolean;
  canCreateTemplates: boolean;
  // User menu
  showUserMenu: boolean;
  setShowUserMenu: (v: boolean) => void;
  userMenuRef: React.RefObject<HTMLDivElement | null>;
  logout: () => void;
  navigate: (path: string) => void;
  // Tabs
  activeTab: DashboardTab;
  setActiveTab: (tab: DashboardTab) => void;
  showTabs: boolean;
  showMastersTab: boolean;
  showUsersTab: boolean;
  showArchiveTab: boolean;
  stats: { total: number; mastersCount: number };
  archivedCount: number;
  // Actions
  onCreateTemplate: () => void;
}

/* ================================================================
   Component
   ================================================================ */

export function DashboardHeader({
  user,
  userAvatar,
  isDark,
  toggleTheme,
  isAdmin,
  canCreateTemplates,
  showUserMenu,
  setShowUserMenu,
  userMenuRef,
  logout,
  navigate,
  activeTab,
  setActiveTab,
  showTabs,
  showMastersTab,
  showUsersTab,
  showArchiveTab,
  stats,
  archivedCount,
  onCreateTemplate,
}: DashboardHeaderProps) {
  return (
    <header
      className="sticky top-0 z-50 border-b border-white/[0.08]"
      style={{
        background: "linear-gradient(135deg, #001C32 0%, #052b7a 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={zapLogo} alt="Zap Group" className="w-9 h-9 rounded-lg shadow-lg shadow-zap/20" />
            <div>
              <h1 className="text-base text-white leading-tight" style={{ fontWeight: 700 }}>
                {"ZAP WEB"}
              </h1>
              <p className="text-[11px] text-white/40 leading-tight">
                {"\u05E0\u05D9\u05D4\u05D5\u05DC \u05D5\u05E2\u05E8\u05D9\u05DB\u05EA \u05D0\u05EA\u05E8\u05D9\u05DD"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Search trigger */}
            <SearchTriggerButton />

            {/* Intake forms link */}
            <span
              role="button"
              tabIndex={0}
              onClick={() => navigate("/intake-forms")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/intake-forms"); }}
              className="w-8 h-8 rounded-lg bg-white/[0.08] border border-white/[0.08] hover:bg-white/[0.14] flex items-center justify-center cursor-pointer transition-all"
              title={"\u05D8\u05E4\u05E1\u05D9 \u05DC\u05E7\u05D5\u05D7\u05D5\u05EA"}
            >
              <ClipboardList className="w-4 h-4 text-white/60" />
            </span>

            {/* Dark mode toggle */}
            <span
              role="button"
              tabIndex={0}
              onClick={toggleTheme}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleTheme(); }}
              className="w-8 h-8 rounded-lg bg-white/[0.08] border border-white/[0.08] hover:bg-white/[0.14] flex items-center justify-center cursor-pointer transition-all"
              title={isDark ? "\u05DE\u05E6\u05D1 \u05D1\u05D4\u05D9\u05E8" : "\u05DE\u05E6\u05D1 \u05DB\u05D4\u05D4"}
            >
              {isDark ? <Sun className="w-4 h-4 text-amber-300" /> : <Moon className="w-4 h-4 text-white/60" />}
            </span>

            {/* Review center link */}
            <span
              role="button"
              tabIndex={0}
              onClick={() => navigate("/review-center")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/review-center"); }}
              className="w-8 h-8 rounded-lg bg-white/[0.08] border border-white/[0.08] hover:bg-white/[0.14] flex items-center justify-center cursor-pointer transition-all"
              title={"\u05DE\u05E8\u05DB\u05D6 \u05E1\u05E7\u05D9\u05E8\u05D4"}
            >
              <MessageSquare className="w-4 h-4 text-white/60" />
            </span>

            {/* Notifications bell */}
            <NotificationBell dark />

            {/* Vault — admin only, discreet */}
            {isAdmin && (
              <span
                role="button"
                tabIndex={0}
                onClick={() => navigate("/vault")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/vault"); }}
                className="w-8 h-8 rounded-lg bg-white/[0.05] border border-white/[0.05] hover:bg-white/[0.14] flex items-center justify-center cursor-pointer transition-all"
              >
                <Lock className="w-3.5 h-3.5 text-white/30 hover:text-white/60" />
              </span>
            )}

            {/* User info badge — clickable with dropdown */}
            <div className="relative hidden sm:block" ref={userMenuRef}>
              <span
                role="button"
                tabIndex={0}
                onClick={() => setShowUserMenu(!showUserMenu)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowUserMenu(!showUserMenu); }}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/[0.08] rounded-lg border border-white/[0.08] hover:bg-white/[0.14] transition-all cursor-pointer"
              >
                <div className="w-6 h-6 rounded-full overflow-hidden bg-white/20 flex items-center justify-center shrink-0">
                  {userAvatar ? (
                    <img src={userAvatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-3.5 h-3.5 text-white/50" />
                  )}
                </div>
                <span className="text-xs text-white/70" style={{ fontWeight: 500 }}>{user.displayName || user.username}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${ROLE_BADGE_COLORS[user.role] || ROLE_BADGE_COLORS.marketer}`} style={{ fontWeight: 600 }}>
                  {ROLE_LABELS[user.role] || user.role}
                </span>
              </span>

              {/* Dropdown menu */}
              {showUserMenu && (
                <div className="absolute top-full left-0 mt-1.5 bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 min-w-[180px] z-50" dir="rtl">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => { setShowUserMenu(false); navigate("/profile"); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setShowUserMenu(false); navigate("/profile"); } }}
                    className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    <UserCircle className="w-4 h-4 text-gray-400" />
                    {"\u05E4\u05E8\u05D5\u05E4\u05D9\u05DC"}
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => { setShowUserMenu(false); navigate("/media-library"); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setShowUserMenu(false); navigate("/media-library"); } }}
                    className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    <ImageIcon className="w-4 h-4 text-gray-400" />
                    {"\u05E1\u05E4\u05E8\u05D9\u05D9\u05EA \u05DE\u05D3\u05D9\u05D4"}
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => { setShowUserMenu(false); navigate("/intake-forms"); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setShowUserMenu(false); navigate("/intake-forms"); } }}
                    className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    <ClipboardList className="w-4 h-4 text-gray-400" />
                    {"\u05D8\u05E4\u05E1\u05D9 \u05DC\u05E7\u05D5\u05D7\u05D5\u05EA"}
                  </span>
                  {isAdmin && (<>
                    <div className="mx-3 my-1 border-t border-gray-100" />
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => { setShowUserMenu(false); navigate("/settings"); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setShowUserMenu(false); navigate("/settings"); } }}
                      className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                    >
                      <Settings className="w-4 h-4 text-gray-400" />
                      {"\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA \u05DE\u05E2\u05E8\u05DB\u05EA"}
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => { setShowUserMenu(false); navigate("/smoke-test"); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setShowUserMenu(false); navigate("/smoke-test"); } }}
                      className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                    >
                      <ClipboardCheck className="w-4 h-4 text-gray-400" />
                      {"Smoke Test"}
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => { setShowUserMenu(false); navigate("/automations"); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setShowUserMenu(false); navigate("/automations"); } }}
                      className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                    >
                      <Zap className="w-4 h-4 text-gray-400" />
                      {"\u05D0\u05D5\u05D8\u05D5\u05DE\u05E6\u05D9\u05D5\u05EA"}
                    </span>
                  </>)}
                  <div className="mx-3 my-1 border-t border-gray-100" />
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => { setShowUserMenu(false); logout(); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setShowUserMenu(false); logout(); } }}
                    className="flex items-center gap-2.5 px-4 py-2 text-sm text-red-500 hover:bg-red-50 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4" />
                    {"\u05D4\u05EA\u05E0\u05EA\u05E7\u05D5\u05EA"}
                  </span>
                </div>
              )}
            </div>

            {/* Mobile-only: compact avatar button -> profile */}
            <span
              role="button"
              tabIndex={0}
              onClick={() => navigate("/profile")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/profile"); }}
              className="sm:hidden w-8 h-8 rounded-full overflow-hidden bg-white/20 flex items-center justify-center shrink-0 cursor-pointer hover:bg-white/30 transition-all"
              title={"\u05E4\u05E8\u05D5\u05E4\u05D9\u05DC"}
            >
              {userAvatar ? (
                <img src={userAvatar} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-4 h-4 text-white/50" />
              )}
            </span>

            {canCreateTemplates && (
              <span
                role="button"
                tabIndex={0}
                onClick={onCreateTemplate}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onCreateTemplate(); }}
                className="flex items-center gap-2 px-4 py-2 bg-white/[0.12] text-white rounded-lg hover:bg-white/[0.2] transition-all text-sm cursor-pointer shadow-md shadow-zap/20 border-2 border-white"
                style={{ fontWeight: 600 }}
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">{"\u05EA\u05D1\u05E0\u05D9\u05EA \u05D7\u05D3\u05E9\u05D4"}</span>
                <span className="sm:hidden">{"\u05D7\u05D3\u05E9\u05D4"}</span>
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        {showTabs && (
          <div className="flex gap-1 -mb-px">
            <TabButton
              active={activeTab === "templates"}
              onClick={() => setActiveTab("templates")}
              icon={<LayoutTemplate className="w-3.5 h-3.5" />}
              label={"\u05EA\u05D1\u05E0\u05D9\u05D5\u05EA"}
              count={stats.total}
            />
            {showMastersTab && (
              <TabButton
                active={activeTab === "masters"}
                onClick={() => setActiveTab("masters")}
                icon={<Crown className="w-3.5 h-3.5" />}
                label={"\u05DE\u05D0\u05E1\u05D8\u05E8"}
                count={stats.mastersCount}
              />
            )}
            {showUsersTab && (
              <TabButton
                active={activeTab === "users"}
                onClick={() => setActiveTab("users")}
                icon={<Users className="w-3.5 h-3.5" />}
                label={"\u05DE\u05E9\u05EA\u05DE\u05E9\u05D9\u05DD"}
              />
            )}
            {showArchiveTab && (
              <TabButton
                active={activeTab === "archive"}
                onClick={() => setActiveTab("archive")}
                icon={<Archive className="w-3.5 h-3.5" />}
                label={"\u05D0\u05E8\u05DB\u05D9\u05D5\u05DF"}
                count={archivedCount}
              />
            )}
          </div>
        )}
      </div>
    </header>
  );
}